import { type FitViewOptions } from '@xyflow/react';
import { type LayoutOptions } from 'elkjs/lib/elk.bundled.js';

/**
 * How the builder canvas fits the diagram in view. A small diagram stays at
 * its true size: the default largest zoom of 2 makes a few tiles fill the
 * canvas.
 */
export const DIAGRAM_FIT_VIEW_OPTIONS: FitViewOptions = {
  padding: 0.15,
  maxZoom: 1,
};

/** Minimum space between tiles and between graph layers, in canvas pixels. */
const LAYOUT_GAP = 40;

/** Space for stage bands and cross-stage edge labels, in canvas pixels. */
export const STAGE_GAP = 160;

/**
 * The height of a stage band node box, in canvas pixels. PhaseBandNode draws
 * the rule and the label against the visible canvas, so the box only needs a
 * height.
 */
export const STAGE_BAND_HEIGHT = 1;

/**
 * The largest distance from the tiles of a stage to the band after it, in
 * canvas pixels. Labels of connections between stages go in the middle of
 * the space between the stages, so the band stays near the earlier stage.
 */
export const STAGE_BAND_OFFSET = LAYOUT_GAP;

/**
 * Minimum space between the tiles of two adjacent stages while the user drags
 * a tile, in canvas pixels. It holds the band and the same space after it.
 */
export const MIN_STAGE_SPACING = STAGE_BAND_OFFSET * 2;

/** Shared ELK settings for measured tile placement and orthogonal edge routes. */
export const ELK_LAYOUT_OPTIONS: LayoutOptions = {
  'elk.algorithm': 'layered',
  // Center measured layer extents before routing and component packing.
  'elk.layered.nodePlacement.strategy': 'SIMPLE',
  'elk.edgeRouting': 'ORTHOGONAL',
  // A fixed seed makes automatic layout repeatable.
  'elk.randomSeed': '42',
  'elk.spacing.nodeNode': String(LAYOUT_GAP),
  'elk.layered.spacing.nodeNodeBetweenLayers': String(LAYOUT_GAP),
  // Minimum edge-to-tile and edge-to-label distances, in canvas pixels.
  'elk.spacing.edgeNode': '20',
  'elk.spacing.edgeLabel': '12',
};

/** Keep each tile's input and output ports at their measured coordinates. */
export const ELK_NODE_LAYOUT_OPTIONS: LayoutOptions = {
  'elk.portConstraints': 'FIXED_POS',
};

/** Place measured labels at the edge center and reserve space in the route. */
export const ELK_LABEL_LAYOUT_OPTIONS: LayoutOptions = {
  'elk.edgeLabels.placement': 'CENTER',
  // Inline is a label option; setting it on the graph is ignored.
  'elk.edgeLabels.inline': 'true',
};
